import { memo, ReactNode } from "react";

import { ClickAwayListener, Tooltip } from "@mui/material";

import Info from "@zenith/assets/icons/Info";

/**
 * Props for Info Tooltip Component.
 * @interface
 * @name InfoTooltipComponentProps
 * @kind interface
 */
interface InfoTooltipComponentProps {
  /**
   * Callback fired when the tooltip opens.
   * @method
   * @name handleTooltipOpen
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @returns {void}
   */
  handleTooltipOpen: () => void;
  /**
   * Callback fired when the tooltip opens.
   * @method
   * @name handleTooltipClose
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @returns {void}
   */
  handleTooltipClose: () => void;
  /**
   * (optional)
   * Callback fired when the popup requests to be closed.
   * @default "6px"
   * @property
   * @name borderRadius
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @type {string | undefined}
   */
  borderRadius?: string;
  /**
   * If `true`, opens the Info Tooltip.
   * @property
   * @name open
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @type {boolean}
   */
  open: boolean;
  /**
   * Id for the Info Tooltip.
   * @property
   * @name tooltipId
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @type {string}
   */
  tooltipId: string;
  /**
   * Content for the Info Tooltip.
   * @property
   * @name content
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @type {ReactNode}
   */
  content?: ReactNode;
  /**
   * (optional)
   * Icon color for the Info Tooltip.
   * @property
   * @name iconColor
   * @kind property
   * @memberof InfoTooltipComponentProps
   * @type {string | undefined}
   */
  iconColor?: string;
  size?: "sm" | "md" | "lg";
  nowrap?: boolean;
  placement?:
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end"
    | "right"
    | "right-start"
    | "right-end";
  title?: ReactNode;
  subtitle?: ReactNode;
}

/**
 * Memoized Info Tooltip Component.
 * @constant
 * @name InfoTooltipComponent
 * @kind variable
 * @type {React.NamedExoticComponent<InfoTooltipComponentProps>}
 */
const InfoTooltipComponent: React.NamedExoticComponent<InfoTooltipComponentProps> =
  memo(
    /**
     * Info Tooltip Component.
     * @function
     * @name InfoTooltipComponent
     * @kind function
     * @memberof InfoTooltipComponent
     * @param {InfoTooltipComponentProps} props Props for Info Tooltip Component.
     * @returns {JSX.Element} Info Tooltip Component.
     */
    function InfoTooltipComponent(
      props: InfoTooltipComponentProps
    ): JSX.Element {
      const {
        handleTooltipClose,
        handleTooltipOpen,
        open,
        content,
        title,
        subtitle,
        tooltipId,
        borderRadius,
        iconColor,
        size = "md",
        nowrap = false,
        placement = "top-start",
      } = props;

      const sizeStyles: Record<
        "sm" | "md" | "lg",
        { fontSize: string; padding: string }
      > = {
        sm: { fontSize: "12px", padding: "8px 8px" },
        md: { fontSize: "13px", padding: "6px 10px" },
        lg: { fontSize: "14px", padding: "8px 14px" },
      };

      const resolvedContent =
        title || subtitle ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            {title && <span style={{ fontWeight: 500 }}>{title}</span>}
            {subtitle && <span style={{ fontWeight: 500 }}>{subtitle}</span>}
          </div>
        ) : (
          content
        );

      const tooltipProps = {
        componentsProps: {
          tooltip: {
            sx: {
              backgroundColor: "#1F2937",
              borderRadius: borderRadius || "6px",
              fontSize: sizeStyles[size].fontSize,
              padding: sizeStyles[size].padding,
              ...(nowrap && { maxWidth: "none" }),
            },
          },
          arrow: {
            sx: { color: "#1F2937" },
          },
        },
        PopperProps: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: ({
                  popper,
                  reference,
                }: {
                  popper: { width: number; height: number };
                  reference: { width: number; height: number };
                }) => {
                  const isTopBottom =
                    placement.startsWith("top") ||
                    placement.startsWith("bottom");
                  const popperSize = isTopBottom ? popper.width : popper.height;
                  const refSize = isTopBottom
                    ? reference.width
                    : reference.height;
                  if (placement.endsWith("-start"))
                    return [refSize / 2 - popperSize * 0.1, 8];
                  if (placement.endsWith("-end"))
                    return [popperSize * 0.1 - refSize / 2, 8];
                  return [0, 8];
                },
              },
            },
          ],
        },
      };

      return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            {...tooltipProps}
            placement={placement}
            arrow
            id={tooltipId}
            disableHoverListener
            title={resolvedContent}
            onClose={handleTooltipClose}
            open={open}
          >
            <button onClick={handleTooltipOpen}>
              <Info />
            </button>
          </Tooltip>
        </ClickAwayListener>
      );
    }
  );

export default InfoTooltipComponent;
